/**
 * In-flight-request deduplication for a "create once" action whose result is
 * later addressed by an id that only exists after the request resolves.
 *
 * Root cause this exists for (scenario-builder duplicate rows, prod
 * 2026-08): the scenario wizard's `'new'` sentinel route resolves a draft's
 * real id from `POST /scenarios`'s response, stored in React state
 * (`setSavedId` in an `onSuccess` callback). Stage navigation, the back
 * link, and "Compute comparison" all independently call the same autosave
 * mutation whenever the draft is dirty. Because `savedId` only updates AFTER
 * the first POST's response lands, a second trigger fired before that
 * response arrives still observes `savedId === null` and takes the SAME
 * "create" branch, POSTing a second, distinct row — no user double-click
 * required, just two ordinary navigations that happen to straddle one
 * network round trip.
 *
 * `runOnce` fixes this at the source rather than at any one call site: every
 * caller that arrives while a create is in flight is handed the SAME
 * promise (and therefore the same resulting row) instead of starting its
 * own request. This holds regardless of which UI path triggered the second
 * call — stage nav, Back, Compute, or the explicit Save button all share
 * one `SingleFlight` instance per draft.
 */
export class SingleFlight<T> {
  private inFlight: Promise<T> | null = null;

  /**
   * Runs `fn` if nothing is in flight; otherwise returns the in-flight
   * promise unchanged. The in-flight slot clears once `fn` settles (success
   * OR failure) so a later, genuinely new attempt (e.g. after a save error)
   * is not permanently blocked.
   */
  runOnce(fn: () => Promise<T>): Promise<T> {
    if (!this.inFlight) {
      this.inFlight = fn().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  /** True while a call is in flight — exposed for tests/diagnostics only. */
  get pending(): boolean {
    return this.inFlight !== null;
  }
}
